import { useSyncExternalStore } from "react";

/** "Who has an unsent draft on this session" presence.
 *
 *  Fed by two sources that reconcile into one store:
 *  - live ``session.typing`` SSE pings (recordTyping) — instant while a
 *    collaborator is actively typing, and
 *  - the persisted ``session_drafts`` fetched from the server (seedDrafts) —
 *    so a viewer that joins/reloads still sees who has an unsent draft, and
 *    for how long it's been sitting.
 *
 *  An entry lives as long as the draft is unsent: it is removed only on a
 *  ``chars<=0`` ping (send/clear) or when a fetch no longer reports it. The
 *  ``at`` timestamp is the last activity — the UI shows "typing…" when it is
 *  recent and "draft · N ago" otherwise (see TypingPresenceBar). */

export type Typer = {
  userId: string;
  name: string;
  chars: number;
  draft: string;
  at: number; // ms epoch of last activity (live ping or persisted updated_at)
};

// A live ping newer than this is kept even if a (slightly older) drafts fetch
// snapshot doesn't include it yet — stops a poll from flickering an active
// typer out between their keystroke and the next fetch.
const PING_GRACE_MS = 4000;

const store = new Map<string, Map<string, Typer>>(); // sessionId -> userId -> Typer
const snapshots = new Map<string, Typer[]>(); // stable arrays per session
const listeners = new Set<() => void>();
const EMPTY: Typer[] = [];

function rebuild(sessionId: string): void {
  const m = store.get(sessionId);
  snapshots.set(sessionId, m && m.size ? [...m.values()] : EMPTY);
}

function emit(): void {
  for (const l of listeners) l();
}

/** Live typing ping. ``chars <= 0`` removes the typer (sent / cleared). */
export function recordTyping(
  sessionId: string,
  t: { userId: string; name: string; chars: number; draft: string },
): void {
  if (!sessionId || !t.userId) return;
  const m = store.get(sessionId);
  if (t.chars <= 0) {
    if (m && m.delete(t.userId)) {
      if (!m.size) store.delete(sessionId);
      rebuild(sessionId);
      emit();
    }
    return;
  }
  const bucket = m ?? new Map<string, Typer>();
  if (!m) store.set(sessionId, bucket);
  bucket.set(t.userId, {
    userId: t.userId,
    name: t.name || "Someone",
    chars: t.chars,
    draft: t.draft ?? "",
    at: Date.now(),
  });
  rebuild(sessionId);
  emit();
}

/** Reconcile with the persisted drafts fetched from the server: add/update
 *  each, and drop store entries the server no longer reports (cleared/sent) —
 *  unless a live ping landed within the grace window (the fetch may predate
 *  it). */
export function seedDrafts(
  sessionId: string,
  drafts: Array<{
    userId: string;
    name: string;
    chars: number;
    draft: string;
    updatedAt: string;
  }>,
): void {
  if (!sessionId) return;
  const bucket = store.get(sessionId) ?? new Map<string, Typer>();
  if (!store.has(sessionId)) store.set(sessionId, bucket);
  const now = Date.now();
  const seen = new Set<string>();
  for (const d of drafts) {
    if (!d.userId) continue;
    seen.add(d.userId);
    const parsed = Date.parse(d.updatedAt);
    const at = Number.isFinite(parsed) ? parsed : now;
    const existing = bucket.get(d.userId);
    bucket.set(d.userId, {
      userId: d.userId,
      name: d.name || existing?.name || "Someone",
      chars: d.chars,
      draft: d.draft ?? "",
      // Keep the fresher of (server updated_at, a very recent live ping).
      at: existing && existing.at > at ? existing.at : at,
    });
  }
  for (const [uid, t] of [...bucket]) {
    if (!seen.has(uid) && now - t.at > PING_GRACE_MS) bucket.delete(uid);
  }
  if (!bucket.size) store.delete(sessionId);
  rebuild(sessionId);
  emit();
}

/** Everyone with an unsent draft on the session (caller filters out self).
 *  Stable reference between changes. */
export function useTypers(sessionId: string | undefined): Typer[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => (sessionId ? snapshots.get(sessionId) ?? EMPTY : EMPTY),
    () => EMPTY,
  );
}
